import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Sparkles, QrCode, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

export default function CheckIn() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const code = params.get("code") || "";
  const [state, setState] = useState("loading"); // loading | success | already | error
  const [result, setResult] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (user === null) return; // still resolving auth
    if (user === false) {
      const next = `/checkin?code=${encodeURIComponent(code)}`;
      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
      return;
    }
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const { data } = await api.post("/member/checkin", { code });
        setResult(data);
        setState(data.already ? "already" : "success");
        await refreshUser();
      } catch (e) {
        setResult({ message: formatApiError(e.response?.data?.detail) || "Check-in failed" });
        setState("error");
      }
    })();
    // eslint-disable-next-line
  }, [user]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone px-6" data-testid="checkin-page">
      <div className="w-full max-w-md bg-white rounded-[1.75rem] border border-slate-100 p-10 text-center shadow-xl shadow-blue-900/5">
        {state === "loading" && (
          <div data-testid="checkin-loading">
            <Loader2 className="h-10 w-10 mx-auto text-iri-mid animate-spin" />
            <p className="mt-5 text-[#5C6664]">Checking you in…</p>
          </div>
        )}

        {(state === "success" || state === "already") && (
          <div data-testid="checkin-success">
            <div className="h-16 w-16 rounded-full iri-gradient mx-auto flex items-center justify-center">
              {state === "success" ? <Sparkles className="h-7 w-7 text-ink" /> : <CheckCircle2 className="h-7 w-7 text-ink" />}
            </div>
            <h1 className="font-heading text-3xl font-light text-ink mt-6">
              {state === "success" ? "You're checked in!" : "Already checked in"}
            </h1>
            <p className="text-[#5C6664] mt-2">{result?.message}</p>
            {state === "success" && (
              <p className="mt-4 font-heading text-5xl font-light text-ink" data-testid="checkin-awarded">
                +{result?.awarded} pts
              </p>
            )}
            <p className="text-sm text-[#8A9492] mt-2">Balance: {result?.points} points</p>
            <Button onClick={() => navigate("/dashboard")} data-testid="checkin-dashboard-btn"
              className="mt-7 iri-gradient text-ink rounded-full h-12 w-full font-medium hover:scale-[1.02] transition-transform">
              View my rewards
            </Button>
          </div>
        )}

        {state === "error" && (
          <div data-testid="checkin-error">
            <div className="h-16 w-16 rounded-full bg-red-50 mx-auto flex items-center justify-center">
              <QrCode className="h-7 w-7 text-red-400" />
            </div>
            <h1 className="font-heading text-2xl font-light text-ink mt-6">Check-in unsuccessful</h1>
            <p className="text-[#5C6664] mt-2">{result?.message}</p>
            <Button asChild className="mt-7 bg-ink text-white rounded-full h-12 w-full">
              <Link to="/dashboard">Go to dashboard</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
